import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useTheme } from "@/context/ThemeContext";
import { User, Mail, MapPin, TrendingUp, Edit2, Heart, Trophy, Target, Activity, Calendar, Users, Award, Zap, Shield, QrCode, Eye, Share2, Sparkles, Image, Video, MessageCircle, UserPlus, Loader2, Play, MoreHorizontal, Archive, Flag, Ban, AlertTriangle, X, Plus, Check } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import PlayerCardDialog from "@/components/PlayerCardDialog";
import EditProfileDialog from "@/components/EditProfileDialog";
import ProfileImageUpload from "@/components/ProfileImageUpload";
import BannerImageUpload from "@/components/BannerImageUpload";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import StoryViewer from "@/components/StoryViewer";
import StoryCreator from "@/components/StoryCreator";

// Ünvan tanımları (backend ile senkron)
const TITLE_MAP = {
  "title_cevik": { name: "Çevik", icon: "🏃", category: "standard" },
  "title_seri_ayak": { name: "Seri Ayak", icon: "⚡", category: "standard" },
  "title_jet": { name: "Jet", icon: "✈️", category: "standard" },
  "title_kilit_adam": { name: "Kilit Adam", icon: "🔑", category: "standard" },
  "title_calim_ustasi": { name: "Çalım Ustası", icon: "🎯", category: "premium" },
  "title_hiz_makinesi": { name: "Hız Makinesi", icon: "💨", category: "premium" },
  "title_derbi_adami": { name: "Derbi Adamı", icon: "🔥", category: "premium" },
  "title_macin_adami": { name: "Maçın Adamı", icon: "🌟", category: "premium" },
  "title_saha_yildizi": { name: "Saha Yıldızı", icon: "⭐", category: "premium" },
  "title_altin_ayak": { name: "Altın Ayak", icon: "🦶", category: "elite" },
  "title_kral": { name: "Kral", icon: "👑", category: "elite" },
  "title_gladyator": { name: "Gladyatör", icon: "⚔️", category: "elite" },
  "title_saha_patronu": { name: "Saha Patronu", icon: "💪", category: "elite" },
  "title_gol_makinesi": { name: "Gol Makinesi", icon: "⚽", category: "elite" },
  "title_altin_krampon": { name: "Altın Krampon", icon: "👟", category: "elite" },
  "title_efsane_oyuncu": { name: "Efsane Oyuncu", icon: "🏆", category: "legendary" },
  "title_futbol_ikonu": { name: "Futbol İkonu", icon: "💎", category: "legendary" },
};

const ProfilePage = ({ user: currentUser, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [matchHistory, setMatchHistory] = useState([]);
  const [isLiked, setIsLiked] = useState(false);
  const [showPlayerCard, setShowPlayerCard] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [qrCodeData, setQRCodeData] = useState(null);
  const [generatingQR, setGeneratingQR] = useState(false);
  
  // Social feed states
  const [userPosts, setUserPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null); // null, 'pending', 'connected'
  const [sendingRequest, setSendingRequest] = useState(false);
  const [connectionCount, setConnectionCount] = useState(0);
  
  // Comment dialog states
  const [commentDialogPost, setCommentDialogPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loadingComments, setLoadingComments] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  
  // Story states
  const [userStories, setUserStories] = useState([]);
  const [showStoryViewer, setShowStoryViewer] = useState(false);
  const [showStoryCreator, setShowStoryCreator] = useState(false);
  const [showProfileImageDialog, setShowProfileImageDialog] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [archivedStories, setArchivedStories] = useState([]);
  const [loadingArchive, setLoadingArchive] = useState(false);
  
  // Öne Çıkanlar (Highlights) state'leri
  const [highlights, setHighlights] = useState([]);
  const [showCreateHighlightDialog, setShowCreateHighlightDialog] = useState(false);
  const [selectedStoriesForHighlight, setSelectedStoriesForHighlight] = useState([]);
  const [newHighlightName, setNewHighlightName] = useState("");
  const [newHighlightCover, setNewHighlightCover] = useState("");
  const [creatingHighlight, setCreatingHighlight] = useState(false);
  const [showHighlightViewer, setShowHighlightViewer] = useState(false);
  const [currentHighlight, setCurrentHighlight] = useState(null);
  const [highlightStories, setHighlightStories] = useState([]);
  
  const [viewingArchivedStory, setViewingArchivedStory] = useState(null);

  // Raporlama state'leri
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportDescription, setReportDescription] = useState("");
  const [submittingReport, setSubmittingReport] = useState(false);
  
  const REPORT_REASONS = [
    { value: "spam", label: "Spam" },
    { value: "sexual_content", label: "Cinsel içerik" },
    { value: "hate_speech", label: "Hakaret/Küfür" },
    { value: "fraud", label: "Dolandırıcılık" },
    { value: "cheating", label: "Hile" },
    { value: "other", label: "Diğer" }
  ];
  
  const isMountedRef = useRef(true);
  
  const [editData, setEditData] = useState({
    name: "",
    position: "",
    preferredFoot: "right",
    playStyle: "balanced",
    profileImage: "",
    location: "",
    city: "",
    district: "",
    age: "",
    weight: "",
    height: "",
    experience: "",
    bio: "",
    themeColor: "#10b981",
    bannerColor: "",
    bannerImage: "",
    stats: {
      speed: 50,
      power: 50,
      stamina: 50,
      technique: 50,
      defense: 50
    }
  });

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    fetchUser();
    fetchMatchHistory();
    fetchUserPosts();
    fetchConnectionStatus();
    fetchConnectionCount();
    fetchUserStories();
    fetchHighlights();
  }, [id]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/users/profile/${id}`);
      
      if (!isMountedRef.current) return;
      
      setUser(response.data);
      setEditData({
        name: response.data.name,
        position: response.data.position || "midfielder",
        preferredFoot: response.data.preferredFoot || "right",
        playStyle: response.data.playStyle || "balanced",
        profileImage: response.data.profileImage || "",
        location: response.data.location || "",
        city: response.data.city || "",
        district: response.data.district || "",
        age: response.data.age || "",
        weight: response.data.weight || "",
        height: response.data.height || "",
        experience: response.data.experience || "",
        bio: response.data.bio || "",
        themeColor: response.data.themeColor || "#10b981",
        bannerColor: response.data.bannerColor || "",
        bannerImage: response.data.bannerImage || "",
        stats: response.data.stats || {
          speed: 50,
          power: 50,
          stamina: 50,
          technique: 50,
          defense: 50
        }
      });
      
      // Check if current user has liked this profile
      if (currentUser.favorites && currentUser.favorites.includes(id)) {
        setIsLiked(true);
      }
    } catch (error) {
      console.error("Error fetching user:", error);
      if (isMountedRef.current) {
        toast.error("Kullanıcı bilgileri yüklenemedi");
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const fetchMatchHistory = async () => {
    try {
      const response = await axios.get(`${API}/matches/user/${id}/history`);
      setMatchHistory(response.data);
    } catch (error) {
      console.error("Error fetching match history:", error);
    }
  };

  const fetchUserPosts = async () => {
    setLoadingPosts(true);
    try {
      const response = await axios.get(`${API}/social/posts/user/${id}?skip=0&limit=20`);
      setUserPosts(response.data?.posts || []);
    } catch (error) {
      console.error("Error fetching user posts:", error);
      setUserPosts([]);
    } finally {
      setLoadingPosts(false);
    }
  };

  const fetchConnectionStatus = async () => {
    if (id === currentUser?.id) return;
    try {
      const response = await axios.get(`${API}/social/connections`);
      const connections = response.data || [];
      const isConnected = connections.some(c => c.userId === id || c.connectedUserId === id);
      if (isConnected) {
        setConnectionStatus('connected');
      } else {
        // Check pending requests
        const pendingRes = await axios.get(`${API}/social/connections/requests`);
        const pending = pendingRes.data || [];
        const hasPending = pending.some(r => r.fromUserId === id || r.toUserId === id);
        setConnectionStatus(hasPending ? 'pending' : null);
      }
    } catch (error) {
      console.error("Error checking connection:", error);
    }
  };

  const fetchConnectionCount = async () => {
    try {
      // Use user-specific connection count endpoint or calculate from connections
      const response = await axios.get(`${API}/social/connections/count/${id}`);
      setConnectionCount(response.data?.count || 0);
    } catch (error) {
      // Fallback: try to get count from general connections if user is viewing own profile
      try {
        if (id === currentUser?.id) {
          const connRes = await axios.get(`${API}/social/connections`);
          setConnectionCount(connRes.data?.count || connRes.data?.connections?.length || 0);
        }
      } catch (e) {
        console.error("Error fetching connection count:", e);
      }
    }
  };

  // Story fonksiyonları
  const fetchUserStories = async () => {
    try {
      const response = await axios.get(`${API}/stories/user/${id}`);
      setUserStories(response.data?.stories || []);
    } catch (error) {
      console.error("Error fetching user stories:", error);
      setUserStories([]);
    }
  };

  const fetchArchivedStories = async () => {
    if (id !== currentUser?.id) return;
    setLoadingArchive(true);
    try {
      const response = await axios.get(`${API}/stories/archive`);
      setArchivedStories(response.data?.stories || []);
    } catch (error) {
      console.error("Error fetching archived stories:", error);
      setArchivedStories([]);
    } finally {
      setLoadingArchive(false);
    }
  };

  const handleOpenStory = () => {
    if (userStories.length > 0) {
      setShowStoryViewer(true);
    }
  };

  const handleProfileAvatarClick = (e) => {
    e.stopPropagation();
    // Hikaye varsa hikayeyi aç, yoksa profil resmini göster
    if (userStories.length > 0) {
      handleOpenStory();
    } else {
      setShowProfileImageDialog(true);
    }
  };

  const handleProfileAvatarLongPress = () => {
    // Uzun basma - seçenekleri göster
    // Bu mobil için context menu gibi çalışacak
  };

  const handleStoryCreated = () => {
    fetchUserStories();
    setShowStoryCreator(false);
    toast.success("Hikaye paylaşıldı!");
  };

  const handleOpenArchive = () => {
    fetchArchivedStories();
    setShowArchiveDialog(true);
  };

  // Öne Çıkanlar (Highlights) fonksiyonları
  const fetchHighlights = async () => {
    try {
      const endpoint = id === currentUser?.id 
        ? `${API}/highlights` 
        : `${API}/highlights/user/${id}`;
      const response = await axios.get(endpoint);
      setHighlights(response.data?.highlights || []);
    } catch (error) {
      console.error("Error fetching highlights:", error);
      setHighlights([]);
    }
  };

  const handleOpenCreateHighlight = () => {
    fetchArchivedStories();
    setSelectedStoriesForHighlight([]);
    setNewHighlightName("");
    setNewHighlightCover("");
    setShowCreateHighlightDialog(true);
  };

  const toggleStorySelection = (story) => {
    setSelectedStoriesForHighlight(prev => {
      const isSelected = prev.some(s => s.id === story.id);
      if (isSelected) {
        return prev.filter(s => s.id !== story.id);
      } else {
        // İlk seçilen hikayenin medyasını kapak olarak ayarla
        if (prev.length === 0 && !newHighlightCover) {
          setNewHighlightCover(story.mediaUrl);
        }
        return [...prev, story];
      }
    });
  };

  const handleCreateHighlight = async () => {
    if (!newHighlightName.trim()) {
      toast.error("Lütfen bir isim girin");
      return;
    }
    if (selectedStoriesForHighlight.length === 0) {
      toast.error("En az bir hikaye seçin");
      return;
    }

    setCreatingHighlight(true);
    try {
      await axios.post(`${API}/highlights`, {
        name: newHighlightName,
        coverImageUrl: newHighlightCover || selectedStoriesForHighlight[0].mediaUrl,
        storyIds: selectedStoriesForHighlight.map(s => s.id)
      });
      toast.success("Öne çıkan oluşturuldu!");
      setShowCreateHighlightDialog(false);
      fetchHighlights();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Öne çıkan oluşturulamadı");
    } finally {
      setCreatingHighlight(false);
    }
  };

  const handleOpenHighlight = async (highlight) => {
    try {
      const response = await axios.get(`${API}/highlights/${highlight.id}`);
      const highlightData = response.data?.highlight;
      if (highlightData?.stories?.length > 0) {
        setCurrentHighlight(highlightData);
        setHighlightStories(highlightData.stories);
        setShowHighlightViewer(true);
      } else {
        // Boş highlight - otomatik sil
        await axios.delete(`${API}/highlights/${highlight.id}`);
        toast.info("Boş öne çıkan silindi");
        fetchHighlights();
      }
    } catch (error) {
      toast.error("Öne çıkan yüklenemedi");
    }
  };

  const handleDeleteHighlight = async (highlightId) => {
    setHighlightToDelete(highlightId);
  };

  const confirmDeleteHighlight = async () => {
    if (!highlightToDelete) return;
    try {
      await axios.delete(`${API}/highlights/${highlightToDelete}`);
      toast.success("Öne çıkan silindi");
      fetchHighlights();
    } catch (error) {
      toast.error("Silinemedi");
    } finally {
      setHighlightToDelete(null);
    }
  };

  // Mevcut highlight'a hikaye ekleme
  const [showAddToHighlight, setShowAddToHighlight] = useState(false);
  const [addToHighlightTarget, setAddToHighlightTarget] = useState(null);
  const [addToHighlightSelected, setAddToHighlightSelected] = useState([]);
  const [highlightToDelete, setHighlightToDelete] = useState(null);

  const handleOpenAddToHighlight = (highlight) => {
    setAddToHighlightTarget(highlight);
    setAddToHighlightSelected([]);
    fetchArchivedStories();
    setShowAddToHighlight(true);
  };

  const handleAddStoriesToHighlight = async () => {
    if (!addToHighlightTarget || addToHighlightSelected.length === 0) return;
    try {
      for (const storyId of addToHighlightSelected) {
        await axios.post(`${API}/highlights/${addToHighlightTarget.id}/stories/${storyId}`);
      }
      toast.success(`${addToHighlightSelected.length} hikaye eklendi`);
      setShowAddToHighlight(false);
      fetchHighlights();
    } catch (error) {
      toast.error("Hikaye eklenemedi");
    }
  };

  const hasActiveStory = userStories.length > 0;

  // Raporlama fonksiyonu
  const handleReportUser = async () => {
    if (!reportReason) {
      toast.error("Lütfen bir sebep seçin");
      return;
    }
    
    setSubmittingReport(true);
    try {
      await axios.post(`${API}/reports`, {
        reportedUserId: id,
        reason: reportReason,
        description: reportDescription
      });
      toast.success("Rapor gönderildi. İnceleme sonucu tarafınıza bildirilecektir.");
      setShowReportDialog(false);
      setReportReason("");
      setReportDescription("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Rapor gönderilemedi");
    } finally {
      setSubmittingReport(false);
    }
  };

  // Engelleme fonksiyonu
  const handleBlockUser = async () => {
    try {
      await axios.post(`${API}/users/${id}/block`);
      toast.success("Kullanıcı engellendi");
      navigate('/');
    } catch (error) {
      toast.error("Engelleme başarısız");
    }
  };

  const handleSendConnectionRequest = async () => {
    setSendingRequest(true);
    try {
      await axios.post(`${API}/social/connections/request/${id}`);
      setConnectionStatus('pending');
      toast.success("Bağlantı isteği gönderildi!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "İstek gönderilemedi");
    } finally {
      setSendingRequest(false);
    }
  };

  const handleLikePost = async (postId) => {
    try {
      const response = await axios.post(`${API}/social/posts/${postId}/like`);
      setUserPosts(prev => prev.map(post => 
        post.id === postId 
          ? { ...post, isLiked: response.data.liked, likeCount: response.data.likeCount }
          : post
      ));
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  // Comment functions
  const openCommentDialog = async (post) => {
    setCommentDialogPost(post);
    setLoadingComments(true);
    try {
      const response = await axios.get(`${API}/social/posts/${post.id}/comments`);
      setComments(response.data?.comments || []);
    } catch (error) {
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !commentDialogPost) return;
    
    setSubmittingComment(true);
    try {
      const response = await axios.post(`${API}/social/posts/${commentDialogPost.id}/comments`, {
        content: newComment
      });
      
      setComments(prev => [...prev, response.data?.comment || response.data]);
      setNewComment("");
      
      // Update comment count in posts
      setUserPosts(prev => prev.map(p => 
        p.id === commentDialogPost.id 
          ? { ...p, commentCount: (p.commentCount || 0) + 1 }
          : p
      ));
      
      toast.success("Yorum eklendi");
    } catch (error) {
      toast.error("Yorum eklenemedi");
    } finally {
      setSubmittingComment(false);
    }
  };

  const formatPostTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = (now - date) / 1000;
    
    if (diff < 60) return "Az önce";
    if (diff < 3600) return `${Math.floor(diff / 60)} dk`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} saat`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} gün`;
    return date.toLocaleDateString('tr-TR');
  };

  const handleSave = async (dataFromDialog) => {
    setSaving(true);
    try {
      // Dialog'dan gelen veri veya mevcut editData'yı kullan
      // onClick event'inden geliyorsa, editData kullan
      const sourceData = (dataFromDialog && dataFromDialog.name) ? dataFromDialog : editData;
      
      // Integer alanları için boş stringleri null yap
      const dataToSend = {
        ...sourceData,
        age: sourceData.age === "" || sourceData.age === undefined ? null : parseInt(sourceData.age) || null,
        weight: sourceData.weight === "" || sourceData.weight === undefined ? null : parseInt(sourceData.weight) || null,
        height: sourceData.height === "" || sourceData.height === undefined ? null : parseInt(sourceData.height) || null,
      };
      
      const response = await axios.put(`${API}/users/profile`, dataToSend);
      
      setUser(response.data);
      setEditing(false);
      // editData'yı da güncelle
      setEditData({
        name: response.data.name,
        position: response.data.position || "midfielder",
        preferredFoot: response.data.preferredFoot || "right",
        playStyle: response.data.playStyle || "balanced",
        profileImage: response.data.profileImage || "",
        location: response.data.location || "",
        city: response.data.city || "",
        district: response.data.district || "",
        age: response.data.age || "",
        weight: response.data.weight || "",
        height: response.data.height || "",
        experience: response.data.experience || "",
        bio: response.data.bio || "",
        themeColor: response.data.themeColor || "#10b981",
        bannerColor: response.data.bannerColor || "",
        bannerImage: response.data.bannerImage || "",
        stats: response.data.stats || {
          speed: 50,
          power: 50,
          stamina: 50,
          technique: 50,
          defense: 50
        }
      });
      toast.success("Profil güncellendi!");
    } catch (error) {
      console.error("Profile update error:", error.response?.data);
      toast.error(error.response?.data?.detail || "Profil güncellenemedi");
    } finally {
      setSaving(false);
    }
  };

  const handleLike = async () => {
    try {
      const response = await axios.post(`${API}/users/${id}/like`);
      setIsLiked(response.data.liked);
      toast.success(response.data.liked ? "Favorilere eklendi" : "Favorilerden çıkarıldı");
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    }
  };

  const handleGenerateQR = async () => {
    setGeneratingQR(true);
    try {
      const response = await axios.post(`${API}/users/generate-qr`);
      setQRCodeData(response.data.qrCode);
      setUser(prev => ({ ...prev, qrCode: response.data.qrCode }));
      setShowQRCode(true);
      toast.success("QR kod oluşturuldu!");
    } catch (error) {
      toast.error("QR kod oluşturulamadı");
    } finally {
      setGeneratingQR(false);
    }
  };

  const handleShowPlayerCard = () => {
    setShowPlayerCard(true);
  };

  const handleImageUpdate = async (imageData) => {
    try {
      const response = await axios.put(`${API}/users/profile`, {
        profileImage: imageData
      });
      setUser(prev => ({ ...prev, profileImage: imageData }));
      toast.success("Profil resmi güncellendi");
    } catch (error) {
      console.error("Image update error:", error);
      throw error;
    }
  };


  const getPositionLabel = (position) => {
    const labels = {
      goalkeeper: "Kaleci",
      defender: "Defans",
      midfielder: "Orta Saha",
      forward: "Forvet",
      substitute: "Yedek"
    };
    return labels[position] || position;
  };

  const getPositionColor = (position) => {
    const colors = {
      goalkeeper: "bg-yellow-500",
      defender: "bg-blue-500",
      midfielder: "bg-green-500",
      forward: "bg-red-500",
      substitute: "bg-gray-500"
    };
    return colors[position] || "bg-gray-500";
  };

  const getFootLabel = (foot) => {
    const labels = { right: "Sağ", left: "Sol", both: "Çift Ayak" };
    return labels[foot] || foot;
  };

  const getStyleLabel = (style) => {
    const labels = {
      aggressive: "Agresif",
      passer: "Pasçı", 
      technical: "Teknik",
      sprinter: "Sprinter",
      balanced: "Dengeli"
    };
    return labels[style] || style;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 dark:bg-dark-bg dark:from-dark-bg dark:to-dark-bg transition-colors duration-300">
        <Navbar user={currentUser} onLogout={onLogout} />
        <div className="flex items-center justify-center min-h-[80vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 dark:bg-dark-bg dark:from-dark-bg dark:to-dark-bg transition-colors duration-300">
        <Navbar user={currentUser} onLogout={onLogout} />
        <div className="container mx-auto px-4 py-8">
          <Card className="p-12 text-center">
            <p className="text-gray-500 text-lg">Kullanıcı bulunamadı</p>
          </Card>
        </div>
      </div>
    );
  }

  const isOwnProfile = currentUser.id === user.id;
  const record = user.record || { goals: 0, assists: 0, saves: 0, mvp: 0, matchesPlayed: 0, totalPoints: 0 };
  const themeColor = user?.themeColor || "#10b981";
  const bannerStyle = user?.bannerImage 
    ? { backgroundImage: `url(${user.bannerImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : isDark 
      ? { background: '#1f2224' }
      : { background: user?.bannerColor || `linear-gradient(to right, ${themeColor}, ${themeColor}dd)` };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-[#1f2224]' : 'bg-gradient-to-br from-slate-50 to-emerald-50'}`} data-testid="profile-page">
      <Navbar user={currentUser} onLogout={onLogout} />
      
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Player Card Header */}
          <Card className={`overflow-hidden mb-6 player-card shadow-2xl ${isDark ? 'bg-[#2a2d2f] border-[#3a3d40]' : ''}`}>
            <div className="relative">
              {/* Card Header with Gradient - Uses user theme */}
              <div className="h-32 sm:h-40 md:h-48 relative overflow-hidden" style={bannerStyle}>
                <div className="absolute inset-0 field-pattern opacity-20"></div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              </div>
              
              {/* Action Buttons - Top Right */}
              <div className="absolute top-2 sm:top-4 right-2 sm:right-4 flex gap-1 sm:gap-2 z-10">
                {!isOwnProfile && (
                  <Button 
                    onClick={handleLike}
                    variant={isLiked ? "default" : "outline"}
                    size="sm"
                    className={`${isLiked ? "bg-red-500 hover:bg-red-600" : isDark ? "bg-[#2a2d2f] border-[#3a3d40] text-white" : "bg-white"} text-xs sm:text-sm`}
                    data-testid="like-profile-btn"
                  >
                    <Heart className={`h-3 w-3 sm:h-4 sm:w-4 ${isLiked ? "fill-current" : ""}`} />
                    <span className="hidden sm:inline ml-1">{isLiked ? "Favorilerde" : "Favoriye Ekle"}</span>
                  </Button>
                )}
                {isOwnProfile && (
                  <>
                    <Button 
                      onClick={handleShowPlayerCard}
                      variant="outline"
                      size="sm"
                      className={`${isDark ? "bg-[#2a2d2f] border-[#3a3d40] text-white" : "bg-white"} text-xs sm:text-sm`}
                    >
                      <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
                      <span className="hidden sm:inline ml-1">Kartımı Gör</span>
                    </Button>
                    <Button 
                      onClick={handleGenerateQR}
                      variant="outline"
                      size="sm"
                      className={`${isDark ? "bg-[#2a2d2f] border-[#3a3d40] text-white" : "bg-white"} text-xs sm:text-sm`}
                    >
                      <QrCode className="h-3 w-3 sm:h-4 sm:w-4" />
                      <span className="hidden sm:inline ml-1">QR Kod</span>
                    </Button>
                  </>
                )}
              </div>
              
              {/* Profile Content - Below Banner */}
              <div className="px-4 sm:px-6 pb-4 sm:pb-6 -mt-14 sm:-mt-20 relative z-20">
                <div className="flex flex-col sm:flex-row sm:items-end gap-3 sm:gap-4">
                  {/* Avatar with Story Ring */}
                  <div className="shrink-0 relative">
                    {isOwnProfile ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="relative group">
                            <div className={`rounded-full p-1 ${hasActiveStory ? 'bg-gradient-to-tr from-emerald-500 via-emerald-400 to-green-500' : ''}`}>
                              <Avatar className={`h-20 w-20 sm:h-28 sm:w-28 md:h-32 md:w-32 border-4 border-white shadow-2xl cursor-pointer transition-transform group-hover:scale-105`}>
                                <AvatarImage src={user.profileImage} />
                                <AvatarFallback className="bg-emerald-600 text-white text-2xl sm:text-4xl font-bold">
                                  {user.name.charAt(0)}
                                </AvatarFallback>
                              </Avatar>
                            </div>
                            {hasActiveStory && (
                              <div className="absolute -top-1 -right-1 bg-emerald-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center border-2 border-white">
                                {userStories.length}
                              </div>
                            )}
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-48">
                          {hasActiveStory && (
                            <DropdownMenuItem onClick={handleOpenStory}>
                              <Play className="h-4 w-4 mr-2 text-emerald-600" />
                              Hikayeni Görüntüle
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => setShowStoryCreator(true)}>
                            <Video className="h-4 w-4 mr-2 text-blue-600" />
                            Hikaye Paylaş
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => setShowProfileImageDialog(true)}>
                            <Eye className="h-4 w-4 mr-2" />
                            Profil Resmini Gör
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={handleOpenArchive}>
                            <Archive className="h-4 w-4 mr-2 text-gray-600" />
                            Arşiv
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={handleOpenCreateHighlight}>
                            <Sparkles className="h-4 w-4 mr-2 text-purple-600" />
                            Öne Çıkan Oluştur
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="relative group">
                            <div className={`rounded-full p-1 ${hasActiveStory ? 'bg-gradient-to-tr from-emerald-500 via-emerald-400 to-green-500' : ''}`}>
                              <Avatar className={`h-20 w-20 sm:h-28 sm:w-28 md:h-32 md:w-32 border-4 border-white shadow-2xl cursor-pointer transition-transform group-hover:scale-105`}>
                                <AvatarImage src={user.profileImage} />
                                <AvatarFallback className="bg-emerald-600 text-white text-2xl sm:text-4xl font-bold">
                                  {user.name.charAt(0)}
                                </AvatarFallback>
                              </Avatar>
                            </div>
                            {hasActiveStory && (
                              <div className="absolute -top-1 -right-1 bg-emerald-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center border-2 border-white">
                                {userStories.length}
                              </div>
                            )}
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-48">
                          {hasActiveStory && (
                            <DropdownMenuItem onClick={handleOpenStory}>
                              <Play className="h-4 w-4 mr-2 text-emerald-600" />
                              Hikayeyi Görüntüle
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => setShowProfileImageDialog(true)}>
                            <Eye className="h-4 w-4 mr-2" />
                            Profil Resmini Gör
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                  
                  {/* User Info */}
                  <div className="flex-1 min-w-0 bg-white/80 sm:bg-transparent p-2 sm:p-0 rounded-lg sm:rounded-none">
                    {/* İsim */}
                    <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 sm:text-white sm:drop-shadow-lg mb-1">{user.name}</h1>
                    
                    {/* Pozisyon ve Rol */}
                    <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
                      <Badge className={`${getPositionColor(user.position)} text-white text-xs px-2 py-0.5`}>
                        {getPositionLabel(user.position)}
                      </Badge>
                      <Badge variant="outline" className="bg-white dark:bg-transparent dark:text-white dark:border-gray-500 text-xs px-2 py-0.5">
                        {user.role === "player" ? "Oyuncu" : user.role === "captain" ? "Kaptan" : "Saha Sahibi"}
                      </Badge>
                    </div>
                    
                    {/* Popülerlik ve Bağlantı - Pozisyonun altında */}
                    <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                      <Badge 
                        variant="outline" 
                        className="bg-white/90 text-emerald-700 text-xs px-2 py-0.5 cursor-pointer hover:bg-emerald-50"
                        onClick={() => navigate('/connections')}
                      >
                        <Users className="h-3 w-3 mr-1" />
                        {connectionCount} Bağlantı
                      </Badge>
                      {(user.popularityPoints > 0 || isOwnProfile) && (
                        <Badge 
                          variant="outline" 
                          className="bg-amber-50 text-amber-700 text-xs px-2 py-0.5 cursor-pointer hover:bg-amber-100"
                          onClick={() => navigate('/rankings')}
                        >
                          ⭐ {user.popularityPoints || 0} Popülerlik
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Aktif Ünvan - Beyaz kısımda, ismin altında */}
                {user.activeTitle && TITLE_MAP[user.activeTitle] && (
                  <div className="mt-3">
                    <Badge 
                      className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm px-3 py-1 flex items-center gap-1.5 shadow-lg animate-pulse w-fit"
                    >
                      <span>{TITLE_MAP[user.activeTitle].icon}</span>
                      <span>{TITLE_MAP[user.activeTitle].name}</span>
                    </Badge>
                  </div>
                )}

                {/* Öne Çıkanlar (Highlights) Bölümü */}
                {(highlights.length > 0 || isOwnProfile) && (
                  <div className="mt-4">
                    <div className="flex items-center gap-3 overflow-x-auto pb-2">
                      {/* Yeni Öne Çıkan Ekle Butonu - Sadece kendi profilinde */}
                      {isOwnProfile && (
                        <button
                          onClick={handleOpenCreateHighlight}
                          className="flex flex-col items-center gap-1 flex-shrink-0"
                        >
                          <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full border-2 border-dashed flex items-center justify-center transition-colors ${isDark ? 'border-[#3a3d40] hover:border-emerald-500 hover:bg-emerald-900/30' : 'border-gray-300 hover:border-emerald-500 hover:bg-emerald-50'}`}>
                            <Sparkles className={`h-6 w-6 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                          </div>
                          <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Yeni</span>
                        </button>
                      )}
                      
                      {/* Öne Çıkanlar Listesi */}
                      {highlights.map((highlight) => (
                        <div key={highlight.id} className="flex flex-col items-center gap-1 flex-shrink-0 relative group">
                          <button
                            onClick={() => handleOpenHighlight(highlight)}
                            className="relative"
                          >
                            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full p-0.5 bg-gradient-to-tr from-purple-500 to-pink-500">
                              <div className={`w-full h-full rounded-full overflow-hidden border-2 ${isDark ? 'border-[#1f2224]' : 'border-white'}`}>
                                {highlight.coverImageUrl ? (
                                  <img
                                    src={highlight.coverImageUrl}
                                    alt={highlight.name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className={`w-full h-full flex items-center justify-center ${isDark ? 'bg-[#2a2d2f]' : 'bg-gray-100'}`}>
                                    <Sparkles className="h-6 w-6 text-purple-500" />
                                  </div>
                                )}
                              </div>
                            </div>
                          </button>
                          <span className={`text-xs max-w-[70px] truncate text-center ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            {highlight.name}
                          </span>
                          {/* Silme ve Ekleme butonları - kendi profilinde her zaman görünür */}
                          {isOwnProfile && (
                            <>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDeleteHighlight(highlight.id); }}
                                className={`absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shadow-md hover:bg-red-600 transition-transform hover:scale-110 z-10 border-2 ${isDark ? 'border-[#1f2224]' : 'border-white'}`}
                                data-testid={`delete-highlight-${highlight.id}`}
                                aria-label="Öne çıkanı sil"
                              >
                                ×
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleOpenAddToHighlight(highlight); }}
                                className={`absolute -top-1 -left-1 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shadow-md transition-transform hover:scale-110 z-10 border-2 ${isDark ? 'border-[#1f2224]' : 'border-white'}`}
                                style={{ background: 'rgb(147,112,219)' }}
                                data-testid={`add-to-highlight-${highlight.id}`}
                                aria-label="Hikaye ekle"
                              >
                                +
                              </button>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Bağlantı, Mesaj, Engelle/Bildir Butonları - Başka kullanıcı için */}
                {!isOwnProfile && (
                  <div className="mt-4 flex items-center gap-2 flex-wrap">
                    {/* Bağlantı İsteği Butonu */}
                    {connectionStatus === 'connected' ? (
                      <Badge className="bg-emerald-100 text-emerald-700 px-3 py-1.5">
                        <Users className="h-4 w-4 mr-1" />
                        Bağlantı
                      </Badge>
                    ) : connectionStatus === 'pending' ? (
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 px-3 py-1.5">
                        <Loader2 className="h-4 w-4 mr-1" />
                        İstek Gönderildi
                      </Badge>
                    ) : (
                      <Button
                        onClick={handleSendConnectionRequest}
                        disabled={sendingRequest}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        size="sm"
                      >
                        {sendingRequest ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <UserPlus className="h-4 w-4 mr-2" />
                        )}
                        Bağlantı İsteği Gönder
                      </Button>
                    )}

                    {/* Mesaj Butonu */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/messages?user=${id}`)}
                    >
                      <MessageCircle className="h-4 w-4 mr-2" />
                      Mesaj
                    </Button>

                    {/* 3 Nokta Menüsü - Engelle/Bildir */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem 
                          onClick={handleBlockUser}
                          className="text-red-600"
                        >
                          <Ban className="h-4 w-4 mr-2" />
                          Engelle
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => setShowReportDialog(true)}
                          className="text-orange-600"
                        >
                          <Flag className="h-4 w-4 mr-2" />
                          Bildir
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Stats Grid - 4'lü: Kondisyon, Maç, Gol, Asist */}
          <Card className={`mb-6 ${isDark ? 'bg-[#2a2d2f] border-[#3a3d40]' : ''}`}>
            <CardContent className="pt-6 pb-6">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                <div className={`text-center p-4 rounded-xl ${isDark ? 'bg-emerald-900/30' : 'bg-gradient-to-br from-emerald-50 to-green-50'}`}>
                  <div className="flex items-center justify-center mb-2">
                    <Activity className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div className="text-2xl font-bold text-emerald-600">{user.kondisyon || 50}</div>
                  <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Kondisyon</div>
                </div>
                <div className={`text-center p-4 rounded-xl ${isDark ? 'bg-blue-900/30' : 'bg-gradient-to-br from-blue-50 to-cyan-50'}`}>
                  <div className="flex items-center justify-center mb-2">
                    <Calendar className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="text-2xl font-bold text-blue-600">{record.matchesPlayed}</div>
                  <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Maç</div>
                </div>
                <div className={`text-center p-4 rounded-xl ${isDark ? 'bg-orange-900/30' : 'bg-gradient-to-br from-orange-50 to-red-50'}`}>
                  <div className="flex items-center justify-center mb-2">
                    <Target className="h-5 w-5 text-orange-600" />
                  </div>
                  <div className="text-2xl font-bold text-orange-600">{record.goals}</div>
                  <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Gol</div>
                </div>
                <div className={`text-center p-4 rounded-xl ${isDark ? 'bg-purple-900/30' : 'bg-gradient-to-br from-purple-50 to-pink-50'}`}>
                  <div className="flex items-center justify-center mb-2">
                    <Users className="h-5 w-5 text-purple-600" />
                  </div>
                  <div className="text-2xl font-bold text-purple-600">{record.assists}</div>
                  <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Asist</div>
                </div>
              </div>

              {/* Sahip Olunan Ünvanlar */}
              {user.titles && user.titles.length > 0 && (
                <div className={`mb-6 p-4 rounded-xl border ${isDark ? 'bg-purple-900/20 border-purple-800' : 'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className={`font-semibold flex items-center gap-2 ${isDark ? 'text-purple-400' : 'text-purple-800'}`}>
                      <Award className="h-5 w-5" />
                      Ünvanlar ({user.titles.length})
                    </h3>
                    {isOwnProfile && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-purple-600 border-purple-300 hover:bg-purple-100"
                        onClick={() => navigate("/titles")}
                      >
                        <Sparkles className="h-4 w-4 mr-1" />
                        Ünvan Al
                      </Button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {user.titles.map((titleId) => {
                      const title = TITLE_MAP[titleId];
                      if (!title) return null;
                      const isActive = user.activeTitle === titleId;
                      return (
                        <Badge 
                          key={titleId}
                          className={`px-3 py-1.5 text-sm ${
                            isActive 
                              ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white' 
                              : isDark 
                                ? 'bg-[#3a3d40] text-gray-200 border border-[#4a4d50]'
                                : 'bg-white text-gray-700 border border-gray-200'
                          }`}
                        >
                          <span className="mr-1">{title.icon}</span>
                          {title.name}
                          {isActive && <Sparkles className="h-3 w-3 ml-1" />}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Ünvan yok - sadece kendi profilinde göster */}
              {isOwnProfile && (!user.titles || user.titles.length === 0) && (
                <div className={`mb-6 p-4 rounded-xl border ${isDark ? 'bg-purple-900/20 border-purple-800' : 'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Award className="h-6 w-6 text-purple-400" />
                      <div>
                        <p className={`font-medium ${isDark ? 'text-purple-300' : 'text-purple-800'}`}>Henüz ünvanın yok</p>
                        <p className={`text-sm ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>Profilini öne çıkar!</p>
                      </div>
                    </div>
                    <Button 
                      className="bg-purple-600 hover:bg-purple-700"
                      onClick={() => navigate("/titles")}
                    >
                      <Sparkles className="h-4 w-4 mr-2" />
                      Ünvan Al
                    </Button>
                  </div>
                </div>
              )}

              {/* Player Details */}
              <div className={`grid md:grid-cols-3 gap-6 mb-6 p-6 rounded-xl ${isDark ? 'bg-[#2a2d2f]' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-emerald-900/30' : 'bg-emerald-100'}`}>
                    <Activity className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Ayak</div>
                    <div className={`font-semibold ${isDark ? 'text-white' : ''}`}>{getFootLabel(user.preferredFoot)}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-purple-900/30' : 'bg-purple-100'}`}>
                    <Zap className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Tarz</div>
                    <div className={`font-semibold ${isDark ? 'text-white' : ''}`}>{getStyleLabel(user.playStyle)}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
                    <MapPin className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Konum</div>
                    <div className={`font-semibold ${isDark ? 'text-white' : ''}`}>{user.location || "Belirtilmemiş"}</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tabs Section */}
          <Tabs defaultValue="feed" className="w-full">
            <TabsList className={`grid w-full grid-cols-4 mb-6 ${isDark ? 'bg-[#2a2d2f]' : ''}`}>
              <TabsTrigger value="feed" className="gap-1">
                <Image className="h-4 w-4" />
                <span className="hidden sm:inline">Paylaşımlar</span>
              </TabsTrigger>
              <TabsTrigger value="stats" className="gap-1">
                <TrendingUp className="h-4 w-4" />
                <span className="hidden sm:inline">İstatistik</span>
              </TabsTrigger>
              <TabsTrigger value="history" className="gap-1">
                <Calendar className="h-4 w-4" />
                <span className="hidden sm:inline">Geçmiş</span>
              </TabsTrigger>
              <TabsTrigger value="edit" disabled={!isOwnProfile} className="gap-1">
                <Edit2 className="h-4 w-4" />
                <span className="hidden sm:inline">Düzenle</span>
              </TabsTrigger>
            </TabsList>

            {/* Social Feed Tab */}
            <TabsContent value="feed">
              <div className="space-y-4">
                {loadingPosts ? (
                  <Card className={`p-8 ${isDark ? 'bg-[#2a2d2f] border-[#3a3d40]' : ''}`}>
                    <div className="flex justify-center">
                      <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
                    </div>
                  </Card>
                ) : userPosts.length === 0 ? (
                  <Card className={`text-center py-12 ${isDark ? 'bg-[#2a2d2f] border-[#3a3d40]' : ''}`}>
                    <CardContent>
                      <Image className={`h-12 w-12 mx-auto mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                      <p className={`mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Henüz paylaşım yok</p>
                      {isOwnProfile && (
                        <Button 
                          onClick={() => navigate('/social')} 
                          className="mt-4 bg-emerald-600 hover:bg-emerald-700"
                        >
                          İlk Paylaşımını Yap
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  userPosts.map((post) => (
                    <Card key={post.id} className={`overflow-hidden ${isDark ? 'bg-[#2a2d2f] border-[#3a3d40]' : ''}`}>
                      {/* Post Header */}
                      <div className="p-4 pb-2">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <Avatar className="h-10 w-10">
                              <AvatarImage src={post.userImage || user?.profileImage} />
                              <AvatarFallback className="bg-emerald-600 text-white">
                                {(post.userName || user?.name)?.charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-semibold text-sm">{post.userName || user?.name}</p>
                              <p className="text-xs text-gray-500">{formatPostTime(post.createdAt)}</p>
                            </div>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => {
                                navigator.clipboard.writeText(window.location.origin + `/post/${post.id}`);
                                toast.success("Link kopyalandı");
                              }}>
                                <Share2 className="h-4 w-4 mr-2" />
                                Paylaş
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>

                      {/* Post Content */}
                      {post.content && (
                        <div className="px-4 pb-2">
                          <p className="text-sm whitespace-pre-wrap">{post.content}</p>
                        </div>
                      )}

                      {/* Media */}
                      {post.mediaUrl && (
                        <div className="relative bg-black">
                          {post.type === "video" ? (
                            <video 
                              src={post.mediaUrl} 
                              className="w-full max-h-[400px] object-contain"
                              controls
                              playsInline
                            />
                          ) : (
                            <img 
                              src={post.mediaUrl} 
                              alt="Post" 
                              className="w-full max-h-[400px] object-contain"
                            />
                          )}
                        </div>
                      )}

                      {/* Post Actions */}
                      <div className="p-4 pt-2">
                        <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
                          <span>{post.likeCount || 0} beğeni</span>
                          <span>{post.commentCount || 0} yorum</span>
                        </div>
                        <div className="flex justify-around pt-2 border-t">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className={post.isLiked ? "text-red-500" : ""}
                            onClick={() => handleLikePost(post.id)}
                          >
                            <Heart className={`h-4 w-4 mr-2 ${post.isLiked ? "fill-current" : ""}`} />
                            Beğen
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openCommentDialog(post)}
                          >
                            <MessageCircle className="h-4 w-4 mr-2" />
                            Yorum
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              navigator.clipboard.writeText(window.location.origin + `/post/${post.id}`);
                              toast.success("Link kopyalandı");
                            }}
                          >
                            <Share2 className="h-4 w-4 mr-2" />
                            Paylaş
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))
                )}
              </div>
            </TabsContent>

            {/* Stats Tab */}
            <TabsContent value="stats">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-emerald-600" />
                    Oyuncu İstatistikleri
                  </CardTitle>
                  <CardDescription>Becerilerin ve performans analizin</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div>
                        <div className="flex justify-between mb-2">
                          <span className="font-medium flex items-center gap-2">
                            <Zap className="h-4 w-4 text-emerald-600" />
                            Hız
                          </span>
                          <span className="text-emerald-600 font-bold text-lg">{user.stats.speed}</span>
                        </div>
                        <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-emerald-500 to-green-600 stat-bar transition-all duration-1000" 
                            style={{ width: `${user.stats.speed}%` }}
                          ></div>
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between mb-2">
                          <span className="font-medium flex items-center gap-2">
                            <Target className="h-4 w-4 text-red-600" />
                            Güç
                          </span>
                          <span className="text-red-600 font-bold text-lg">{user.stats.power}</span>
                        </div>
                        <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-red-500 to-orange-600 stat-bar transition-all duration-1000" 
                            style={{ width: `${user.stats.power}%` }}
                          ></div>
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between mb-2">
                          <span className="font-medium flex items-center gap-2">
                            <Activity className="h-4 w-4 text-blue-600" />
                            Dayanıklılık
                          </span>
                          <span className="text-blue-600 font-bold text-lg">{user.stats.stamina}</span>
                        </div>
                        <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-blue-500 to-cyan-600 stat-bar transition-all duration-1000" 
                            style={{ width: `${user.stats.stamina}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-6">
                      <div>
                        <div className="flex justify-between mb-2">
                          <span className="font-medium flex items-center gap-2">
                            <Award className="h-4 w-4 text-purple-600" />
                            Teknik
                          </span>
                          <span className="text-purple-600 font-bold text-lg">{user.stats.technique}</span>
                        </div>
                        <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-purple-500 to-pink-600 stat-bar transition-all duration-1000" 
                            style={{ width: `${user.stats.technique}%` }}
                          ></div>
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between mb-2">
                          <span className="font-medium flex items-center gap-2">
                            <Shield className="h-4 w-4 text-gray-600" />
                            Defans
                          </span>
                          <span className="text-gray-600 font-bold text-lg">{user.stats.defense}</span>
                        </div>
                        <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-gray-500 to-slate-600 stat-bar transition-all duration-1000" 
                            style={{ width: `${user.stats.defense}%` }}
                          ></div>
                        </div>
                      </div>

                      {/* Overall Rating */}
                      <div className="mt-6 p-6 bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl border-2 border-emerald-200">
                        <div className="text-center">
                          <div className="text-sm text-gray-600 mb-2">Genel Değerlendirme</div>
                          <div className="text-5xl font-bold text-emerald-600 mb-1">
                            {Math.round((user.stats.speed + user.stats.power + user.stats.stamina + user.stats.technique + user.stats.defense) / 5)}
                          </div>
                          <div className="text-xs text-gray-500">100 üzerinden</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Performance Records */}
                  <div className="mt-8 pt-8 border-t">
                    <h3 className="font-semibold mb-4 text-lg flex items-center gap-2">
                      <Trophy className="h-5 w-5 text-yellow-600" />
                      Performans Rekorları
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg text-center">
                        <Trophy className="h-6 w-6 text-yellow-600 mx-auto mb-2" />
                        <div className="text-2xl font-bold text-yellow-600">{record.totalPoints}</div>
                        <div className="text-xs text-gray-600 mt-1">Toplam Puan</div>
                      </div>
                      <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg text-center">
                        <Target className="h-6 w-6 text-green-600 mx-auto mb-2" />
                        <div className="text-2xl font-bold text-green-600">{record.goals}</div>
                        <div className="text-xs text-gray-600 mt-1">Goller</div>
                      </div>
                      <div className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg text-center">
                        <Users className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                        <div className="text-2xl font-bold text-blue-600">{record.assists}</div>
                        <div className="text-xs text-gray-600 mt-1">Asistler</div>
                      </div>
                      <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg text-center">
                        <Shield className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                        <div className="text-2xl font-bold text-purple-600">{record.saves}</div>
                        <div className="text-xs text-gray-600 mt-1">Kurtarışlar</div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Match History Tab */}
            <TabsContent value="history">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-blue-600" />
                    Maç Geçmişi
                  </CardTitle>
                  <CardDescription>Oynanan maçlar ve performans detayları</CardDescription>
                </CardHeader>
                <CardContent>
                  {matchHistory.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">Henüz tamamlanmış maç yok</p>
                  ) : (
                    <div className="space-y-4">
                      {matchHistory.slice(0, 10).map((match) => {
                        const playerPerf = match.performances?.find(p => p.userId === id);
                        return (
                          <div 
                            key={match.id}
                            className="p-4 border rounded-lg hover:border-emerald-500 hover:shadow-md transition-all cursor-pointer"
                            onClick={() => navigate(`/matches/${match.id}`)}
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <h3 className="font-semibold text-lg">{match.venueName}</h3>
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                  <Calendar className="h-4 w-4" />
                                  {match.date} - {match.time}
                                </div>
                              </div>
                              {match.teamScore !== null && (
                                <Badge variant="outline" className="text-lg font-bold">
                                  {match.teamScore} - {match.opponentScore}
                                </Badge>
                              )}
                            </div>
                            {playerPerf && (
                              <div className="flex flex-wrap gap-2 mt-3">
                                {playerPerf.goals > 0 && (
                                  <Badge className="bg-green-100 text-green-700">
                                    ⚽ {playerPerf.goals} Gol
                                  </Badge>
                                )}
                                {playerPerf.assists > 0 && (
                                  <Badge className="bg-blue-100 text-blue-700">
                                    🎯 {playerPerf.assists} Asist
                                  </Badge>
                                )}
                                {playerPerf.mvp && (
                                  <Badge className="bg-yellow-100 text-yellow-700">
                                    🏆 MVP
                                  </Badge>
                                )}
                                <Badge className="bg-emerald-100 text-emerald-700">
                                  +{playerPerf.totalPoints} Puan
                                </Badge>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Edit Tab */}
            <TabsContent value="edit">
              {isOwnProfile && (
                <Card>
                  <CardHeader>
                    <CardTitle>Profili Düzenle</CardTitle>
                    <CardDescription>Bilgilerini ve istatistiklerini güncelle</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name">Ad Soyad</Label>
                        <Input
                          id="name"
                          value={editData.name}
                          onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                          className="mt-2"
                          data-testid="edit-name-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="location">Konum</Label>
                        <Input
                          id="location"
                          value={editData.location}
                          onChange={(e) => setEditData({ ...editData, location: e.target.value })}
                          placeholder="İstanbul, Kadıköy"
                          className="mt-2"
                          data-testid="edit-location-input"
                        />
                      </div>
                    </div>

                    {/* New Physical & Location Fields */}
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="city">İl</Label>
                        <Select 
                          value={editData.city || ""}
                          onValueChange={(val) => setEditData({ ...editData, city: val })}
                        >
                          <SelectTrigger className="mt-2">
                            <SelectValue placeholder="İl seçin" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="İstanbul">İstanbul</SelectItem>
                            <SelectItem value="Ankara">Ankara</SelectItem>
                            <SelectItem value="İzmir">İzmir</SelectItem>
                            <SelectItem value="Bursa">Bursa</SelectItem>
                            <SelectItem value="Antalya">Antalya</SelectItem>
                            <SelectItem value="Adana">Adana</SelectItem>
                            <SelectItem value="Konya">Konya</SelectItem>
                            <SelectItem value="Gaziantep">Gaziantep</SelectItem>
                            <SelectItem value="Kayseri">Kayseri</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="district">İlçe</Label>
                        <Input
                          id="district"
                          value={editData.district || ""}
                          onChange={(e) => setEditData({ ...editData, district: e.target.value })}
                          placeholder="İlçe"
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="age">Yaş</Label>
                        <Input
                          id="age"
                          type="number"
                          value={editData.age || ""}
                          onChange={(e) => setEditData({ ...editData, age: parseInt(e.target.value) || "" })}
                          placeholder="25"
                          className="mt-2"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="height">Boy (cm)</Label>
                        <Input
                          id="height"
                          type="number"
                          value={editData.height || ""}
                          onChange={(e) => setEditData({ ...editData, height: parseInt(e.target.value) || "" })}
                          placeholder="175"
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="weight">Kilo (kg)</Label>
                        <Input
                          id="weight"
                          type="number"
                          value={editData.weight || ""}
                          onChange={(e) => setEditData({ ...editData, weight: parseInt(e.target.value) || "" })}
                          placeholder="70"
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="experience">Deneyim</Label>
                        <Select 
                          value={editData.experience || ""}
                          onValueChange={(val) => setEditData({ ...editData, experience: val })}
                        >
                          <SelectTrigger className="mt-2">
                            <SelectValue placeholder="Seviye seçin" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Beginner">Başlangıç</SelectItem>
                            <SelectItem value="Amateur">Amatör</SelectItem>
                            <SelectItem value="Semi-Pro">Yarı Profesyonel</SelectItem>
                            <SelectItem value="Professional">Profesyonel</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="bio">Biyografi</Label>
                      <Textarea
                        id="bio"
                        value={editData.bio || ""}
                        onChange={(e) => setEditData({ ...editData, bio: e.target.value })}
                        placeholder="Kendinden bahset..."
                        className="mt-2"
                        rows={3}
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="themeColor">Tema Rengi</Label>
                        <div className="flex gap-2 mt-2">
                          <Input
                            id="themeColor"
                            type="color"
                            value={editData.themeColor || "#10b981"}
                            onChange={(e) => setEditData({ ...editData, themeColor: e.target.value })}
                            className="h-10 w-20"
                          />
                          <Input
                            value={editData.themeColor || "#10b981"}
                            onChange={(e) => setEditData({ ...editData, themeColor: e.target.value })}
                            placeholder="#10b981"
                            className="flex-1"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="bannerColor">Banner Rengi</Label>
                        <div className="flex gap-2 mt-2">
                          <Input
                            id="bannerColor"
                            type="color"
                            value={editData.bannerColor || "#10b981"}
                            onChange={(e) => setEditData({ ...editData, bannerColor: e.target.value })}
                            className="h-10 w-20"
                          />
                          <Input
                            value={editData.bannerColor || ""}
                            onChange={(e) => setEditData({ ...editData, bannerColor: e.target.value })}
                            placeholder="#10b981 (opsiyonel)"
                            className="flex-1"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Banner Fotoğrafı Yükleme */}
                    <div>
                      <Label>Banner Fotoğrafı</Label>
                      <p className="text-xs text-gray-500 mb-2">Fotoğraf yüklerseniz renk yerine fotoğraf gösterilir. Kırpma ve konumlandırma yapabilirsiniz.</p>
                      <BannerImageUpload
                        currentImage={editData.bannerImage}
                        bannerColor={editData.bannerColor}
                        themeColor={editData.themeColor}
                        onImageUpdate={(imageData) => setEditData({ ...editData, bannerImage: imageData })}
                      />
                    </div>

                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="position">Pozisyon</Label>
                        <Select 
                          value={editData.position} 
                          onValueChange={(val) => setEditData({ ...editData, position: val })}
                        >
                          <SelectTrigger className="mt-2" data-testid="edit-position-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="goalkeeper">Kaleci</SelectItem>
                            <SelectItem value="defender">Defans</SelectItem>
                            <SelectItem value="midfielder">Orta Saha</SelectItem>
                            <SelectItem value="forward">Forvet</SelectItem>
                            <SelectItem value="substitute">Yedek</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="foot">Ayak</Label>
                        <Select 
                          value={editData.preferredFoot} 
                          onValueChange={(val) => setEditData({ ...editData, preferredFoot: val })}
                        >
                          <SelectTrigger className="mt-2" data-testid="edit-foot-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="right">Sağ</SelectItem>
                            <SelectItem value="left">Sol</SelectItem>
                            <SelectItem value="both">Çift Ayak</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="style">Oyun Tarzı</Label>
                        <Select 
                          value={editData.playStyle} 
                          onValueChange={(val) => setEditData({ ...editData, playStyle: val })}
                        >
                          <SelectTrigger className="mt-2" data-testid="edit-style-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="aggressive">Agresif</SelectItem>
                            <SelectItem value="passer">Pasçı</SelectItem>
                            <SelectItem value="technical">Teknik</SelectItem>
                            <SelectItem value="sprinter">Sprinter</SelectItem>
                            <SelectItem value="balanced">Dengeli</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="profileImage">Profil Resmi URL</Label>
                      <Input
                        id="profileImage"
                        value={editData.profileImage}
                        onChange={(e) => setEditData({ ...editData, profileImage: e.target.value })}
                        placeholder="https://example.com/image.jpg"
                        className="mt-2"
                        data-testid="edit-image-input"
                      />
                    </div>

                    <div>
                      <h3 className="font-semibold mb-4 text-lg">İstatistikler</h3>
                      <div className="space-y-4">
                        {Object.keys(editData.stats).map((stat) => (
                          <div key={stat}>
                            <div className="flex justify-between mb-2">
                              <Label>
                                {stat === "speed" ? "Hız" : 
                                 stat === "power" ? "Güç" : 
                                 stat === "stamina" ? "Dayanıklılık" : 
                                 stat === "technique" ? "Teknik" : "Defans"}
                              </Label>
                              <span className="font-bold text-emerald-600">{editData.stats[stat]}</span>
                            </div>
                            <Input
                              type="range"
                              min="0"
                              max="100"
                              value={editData.stats[stat]}
                              onChange={(e) => setEditData({
                                ...editData,
                                stats: { ...editData.stats, [stat]: parseInt(e.target.value) }
                              })}
                              data-testid={`edit-stat-${stat}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    <Button 
                      onClick={handleSave}
                      disabled={saving}
                      className="w-full bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700"
                      data-testid="save-profile-btn"
                    >
                      {saving ? "Kaydediliyor..." : "Değişiklikleri Kaydet"}
                    </Button>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>
      {/* Player Card Dialog */}
      <PlayerCardDialog 
        open={showPlayerCard} 
        onOpenChange={setShowPlayerCard}
        user={{
          ...user,
          connectionCount: connectionCount
        }}
      />

      {/* Edit Profile Dialog */}
      <EditProfileDialog 
        open={editing}
        onOpenChange={setEditing}
        user={user}
        onSave={handleSave}
      />

      {/* QR Code Dialog */}
      <Dialog open={showQRCode} onOpenChange={setShowQRCode}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Profil QR Kodu</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-4">
            {qrCodeData || user?.qrCode ? (
              <>
                <img 
                  src={qrCodeData || user?.qrCode} 
                  alt="Profile QR Code" 
                  className="w-64 h-64 border-4 border-gray-200 rounded-lg"
                />
                <p className="text-sm text-gray-600 text-center">
                  Bu QR kodu paylaşarak profilini kolayca tanıtabilirsin
                </p>
                
                {/* Share Buttons */}
                <div className="w-full space-y-2">
                  <p className="text-sm font-semibold text-center mb-2">Paylaş:</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      className="w-full bg-green-50 hover:bg-green-100"
                      onClick={() => {
                        const url = `https://fieldmaster.app/profile/${user.id}`;
                        const text = `${user.name} - Field Master Profilim`;
                        window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`, '_blank');
                      }}
                    >
                      <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                      </svg>
                      WhatsApp
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full bg-blue-50 hover:bg-blue-100"
                      onClick={() => {
                        const url = `https://fieldmaster.app/profile/${user.id}`;
                        const text = `${user.name} - Field Master Profilim`;
                        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
                      }}
                    >
                      <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                      </svg>
                      Twitter
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full bg-pink-50 hover:bg-pink-100 col-span-2"
                      onClick={() => {
                        const url = `${window.location.origin}/profile/${user.id}`;
                        navigator.clipboard.writeText(url)
                          .then(() => toast.success("Profil linki kopyalandı!"))
                          .catch(() => toast.error("Link kopyalanamadı"));
                      }}
                    >
                      <Share2 className="h-5 w-5 mr-2" />
                      Link Kopyala
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-gray-500">QR kod yükleniyor...</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Comment Dialog */}
      <Dialog open={!!commentDialogPost} onOpenChange={(open) => !open && setCommentDialogPost(null)}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Yorumlar</DialogTitle>
          </DialogHeader>
          {commentDialogPost && (
            <div className="flex flex-col flex-1 overflow-hidden">
              {/* Post Preview */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg mb-3">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={commentDialogPost.userImage} />
                  <AvatarFallback>{commentDialogPost.userName?.charAt(0)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm">{commentDialogPost.userName}</p>
                  <p className="text-sm text-gray-600 truncate">{commentDialogPost.content}</p>
                </div>
              </div>
              
              {/* Comments List */}
              <div className="flex-1 overflow-y-auto space-y-3 mb-3 max-h-[300px]">
                {loadingComments ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
                  </div>
                ) : comments.length === 0 ? (
                  <p className="text-center text-gray-500 py-4">Henüz yorum yok</p>
                ) : (
                  comments.map((comment) => (
                    <div key={comment.id} className="flex gap-2">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={comment.userImage} />
                        <AvatarFallback>{comment.userName?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 bg-gray-100 rounded-lg p-2">
                        <p className="font-semibold text-sm">{comment.userName}</p>
                        <p className="text-sm">{comment.content}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatPostTime(comment.createdAt)}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              {/* Add Comment */}
              <div className="flex gap-2 pt-3 border-t">
                <Input
                  placeholder="Yorum yaz..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                />
                <Button 
                  onClick={handleAddComment}
                  disabled={submittingComment || !newComment.trim()}
                  className="bg-emerald-500 hover:bg-emerald-600"
                >
                  {submittingComment ? <Loader2 className="h-4 w-4 animate-spin" /> : "Gönder"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Story Viewer */}
      <StoryViewer
        open={showStoryViewer}
        onClose={() => setShowStoryViewer(false)}
        users={[{
          userId: user?.id,
          userName: user?.name,
          userAvatar: user?.profileImage,
          stories: userStories,
          hasUnviewed: false
        }]}
        initialUserIndex={0}
        currentUserId={currentUser?.id}
      />

      {/* Story Creator */}
      <StoryCreator
        open={showStoryCreator}
        onClose={() => setShowStoryCreator(false)}
        onStoryCreated={handleStoryCreated}
        user={currentUser}
      />

      {/* Profile Image Dialog */}
      <Dialog open={showProfileImageDialog} onOpenChange={setShowProfileImageDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Profil Resmi</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center py-4">
            <img 
              src={user?.profileImage || '/default-avatar.png'} 
              alt={user?.name}
              className="max-w-full max-h-[60vh] rounded-lg object-contain"
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Archive Dialog */}
      <Dialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="h-5 w-5" />
              Arşivlenmiş Hikayeler
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto">
            {loadingArchive ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
              </div>
            ) : archivedStories.length === 0 ? (
              <div className="text-center py-8">
                <Archive className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500">Arşivlenmiş hikaye bulunmuyor</p>
                <p className="text-sm text-gray-400 mt-1">Hikayelerinizi arşivleyerek burada saklayabilirsiniz</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {archivedStories.map((story) => (
                  <div 
                    key={story.id} 
                    className="aspect-[9/16] rounded-lg overflow-hidden bg-gray-100 relative group cursor-pointer"
                    onClick={() => {
                      setShowArchiveDialog(false);
                      setViewingArchivedStory(story);
                    }}
                    data-testid={`archived-story-${story.id}`}
                  >
                    {story.mediaType === 'video' ? (
                      <video 
                        src={story.mediaUrl} 
                        className="w-full h-full object-cover"
                        muted
                      />
                    ) : (
                      <img 
                        src={story.mediaUrl} 
                        alt="" 
                        className="w-full h-full object-cover"
                      />
                    )}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Eye className="h-6 w-6 text-white" />
                    </div>
                    <div className="absolute bottom-1 left-1 right-1">
                      <p className="text-white text-xs truncate bg-black/50 px-1 rounded">
                        {new Date(story.createdAt).toLocaleDateString('tr-TR')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Arşivlenmiş Hikaye Görüntüleme - Fixed overlay (Dialog içinde Dialog stacking sorununu önlemek için) */}
      {viewingArchivedStory && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
          onClick={() => setViewingArchivedStory(null)}
          data-testid="archived-story-viewer"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setViewingArchivedStory(null); }}
            className="absolute top-4 right-4 z-50 w-10 h-10 rounded-full flex items-center justify-center bg-black/60 text-white hover:bg-black/80"
            data-testid="close-archived-story"
            aria-label="Kapat"
          >
            <X className="h-5 w-5" />
          </button>
          <div
            className="relative max-w-md w-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {viewingArchivedStory.mediaType === 'video' ? (
              <video src={viewingArchivedStory.mediaUrl} controls autoPlay className="max-w-full max-h-[85vh] object-contain rounded-lg" />
            ) : (
              <img src={viewingArchivedStory.mediaUrl} alt="" className="max-w-full max-h-[85vh] object-contain rounded-lg" />
            )}
            {viewingArchivedStory.content && (
              <div className="absolute bottom-4 left-4 right-4 bg-black/60 rounded-lg p-2">
                <p className="text-white text-sm">{viewingArchivedStory.content}</p>
              </div>
            )}
            <div className="absolute top-2 left-2 bg-black/60 rounded px-2 py-1">
              <p className="text-white text-xs">
                {new Date(viewingArchivedStory.createdAt).toLocaleDateString('tr-TR')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Highlight'a Hikaye Ekle Dialog */}
      <Dialog open={showAddToHighlight} onOpenChange={setShowAddToHighlight}>
        <DialogContent className={`max-w-lg max-h-[80vh] overflow-hidden flex flex-col ${isDark ? 'bg-[#2a2d2f] border-[#3a3d40]' : ''}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: 'rgba(147,112,219,0.9)' }}>
              <Plus className="h-5 w-5" />
              {addToHighlightTarget?.name} - Hikaye Ekle
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto py-2">
            {archivedStories.length === 0 ? (
              <p className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Eklenebilecek arşivlenmiş hikaye yok</p>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {archivedStories.filter(s => !addToHighlightTarget?.storyIds?.includes(s.id)).map(story => (
                  <div 
                    key={story.id}
                    onClick={() => setAddToHighlightSelected(prev => prev.includes(story.id) ? prev.filter(id => id !== story.id) : [...prev, story.id])}
                    className={`aspect-[9/16] rounded-lg overflow-hidden relative cursor-pointer border-2 ${addToHighlightSelected.includes(story.id) ? 'border-purple-500' : 'border-transparent'}`}
                  >
                    {story.mediaType === 'video' ? (
                      <video src={story.mediaUrl} className="w-full h-full object-cover" muted />
                    ) : (
                      <img src={story.mediaUrl} alt="" className="w-full h-full object-cover" />
                    )}
                    {addToHighlightSelected.includes(story.id) && (
                      <div className="absolute inset-0 bg-purple-500/30 flex items-center justify-center">
                        <Check className="h-6 w-6 text-white" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setShowAddToHighlight(false)}>İptal</Button>
            <Button 
              className="flex-1 text-white border-0"
              style={{ background: 'rgba(147,112,219,0.7)' }}
              disabled={addToHighlightSelected.length === 0}
              onClick={handleAddStoriesToHighlight}
            >
              {addToHighlightSelected.length} Hikaye Ekle
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Highlight Dialog */}
      <Dialog open={showCreateHighlightDialog} onOpenChange={setShowCreateHighlightDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-600">
              <Sparkles className="h-5 w-5" />
              Öne Çıkan Oluştur
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 overflow-y-auto flex-1">
            {/* İsim girişi */}
            <div className="space-y-2">
              <Label>Öne Çıkan Adı *</Label>
              <Input
                value={newHighlightName}
                onChange={(e) => setNewHighlightName(e.target.value)}
                placeholder="Örn: Maç Anları, En İyi Goller..."
                maxLength={30}
              />
            </div>

            {/* Arşivden hikaye seçimi */}
            <div className="space-y-2">
              <Label>Arşivden Hikaye Seç ({selectedStoriesForHighlight.length} seçili)</Label>
              {loadingArchive ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
                </div>
              ) : archivedStories.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <Archive className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                  <p className="text-gray-500">Arşivde hikaye yok</p>
                  <p className="text-sm text-gray-400 mt-1">Önce hikaye paylaşıp arşive ekleyin</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto p-2 bg-gray-50 rounded-lg">
                  {archivedStories.map((story) => {
                    const isSelected = selectedStoriesForHighlight.some(s => s.id === story.id);
                    return (
                      <button
                        key={story.id}
                        onClick={() => toggleStorySelection(story)}
                        className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                          isSelected ? 'border-purple-500 ring-2 ring-purple-200' : 'border-transparent hover:border-gray-300'
                        }`}
                      >
                        {story.mediaType === "video" ? (
                          <video src={story.mediaUrl} className="w-full h-full object-cover" muted />
                        ) : (
                          <img src={story.mediaUrl} alt="" className="w-full h-full object-cover" />
                        )}
                        {isSelected && (
                          <div className="absolute inset-0 bg-purple-500/30 flex items-center justify-center">
                            <div className="bg-purple-500 rounded-full p-1">
                              <Eye className="h-4 w-4 text-white" />
                            </div>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Kapak resmi önizleme */}
            {selectedStoriesForHighlight.length > 0 && (
              <div className="space-y-2">
                <Label>Kapak Resmi</Label>
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-purple-500">
                    <img 
                      src={newHighlightCover || selectedStoriesForHighlight[0]?.mediaUrl} 
                      alt="Cover" 
                      className="w-full h-full object-cover" 
                    />
                  </div>
                  <p className="text-sm text-gray-500">İlk seçilen hikaye kapak olarak kullanılacak</p>
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-2 justify-end pt-4 border-t">
            <Button variant="outline" onClick={() => setShowCreateHighlightDialog(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleCreateHighlight}
              disabled={!newHighlightName.trim() || selectedStoriesForHighlight.length === 0 || creatingHighlight}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {creatingHighlight ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Oluşturuluyor...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Oluştur
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Highlight Silme Onay Dialog'u */}
      <AlertDialog open={!!highlightToDelete} onOpenChange={(open) => { if (!open) setHighlightToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Öne Çıkanı Sil</AlertDialogTitle>
            <AlertDialogDescription>
              Bu öne çıkanı silmek istediğinize emin misiniz? Hikayeler arşivinizde kalacaktır, sadece bu öne çıkan klasörü silinecek.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-highlight">İptal</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteHighlight} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-highlight">
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Highlight Story Viewer */}
      {showHighlightViewer && highlightStories.length > 0 && (
        <StoryViewer
          open={showHighlightViewer}
          onClose={() => {
            setShowHighlightViewer(false);
            setCurrentHighlight(null);
            setHighlightStories([]);
          }}
          users={[{
            userId: currentHighlight?.userId || user?.id,
            userName: currentHighlight?.name || "Öne Çıkan",
            userAvatar: user?.profileImage,
            stories: highlightStories.map(s => ({
              ...s,
              userId: currentHighlight?.userId || user?.id
            }))
          }]}
          initialUserIndex={0}
          currentUserId={currentUser?.id}
        />
      )}

      {/* Report User Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <Flag className="h-5 w-5" />
              Kullanıcıyı Bildir
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Sebep Seçin *</Label>
              <Select value={reportReason} onValueChange={setReportReason}>
                <SelectTrigger>
                  <SelectValue placeholder="Bir sebep seçin" />
                </SelectTrigger>
                <SelectContent>
                  {REPORT_REASONS.map(reason => (
                    <SelectItem key={reason.value} value={reason.value}>
                      {reason.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Açıklama (Opsiyonel)</Label>
              <Textarea
                value={reportDescription}
                onChange={(e) => setReportDescription(e.target.value)}
                placeholder="Detaylı açıklama yazabilirsiniz..."
                rows={3}
                maxLength={500}
              />
              <p className="text-xs text-gray-500 text-right">{reportDescription.length}/500</p>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
              <div className="flex gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-500 flex-shrink-0" />
                <p className="text-sm text-orange-700">
                  Raporunuz admin ekibi tarafından incelenecektir. Yanlış veya kötü niyetli raporlar hesap askıya alınmasına neden olabilir.
                </p>
              </div>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setShowReportDialog(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleReportUser}
              disabled={!reportReason || submittingReport}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {submittingReport ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Gönderiliyor...
                </>
              ) : (
                <>
                  <Flag className="h-4 w-4 mr-2" />
                  Rapor Gönder
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfilePage;
